import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import StaticPage from '../PageBuilder/StaticPage';
import pageBuilderCss from '../PageBuilder/PageBuilder.module.css';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { LayoutComposer, ListingCard, Loader, NamedLink } from '../../components';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import heroImage from '../../assets/wedly-hero-image-4.png';
import heroMobileImage from '../../assets/wedly-hero-mobile.jpeg';
import editorialImg1 from '../../assets/wedly-home-12.jpeg';
import editorialImg2 from '../../assets/wedly-home-11.jpeg';
import editorialImg3 from '../../assets/wedly-home-9.jpeg';

import categoryImg1 from '../../assets/wedly-home-1.jpeg';
import categoryImg2 from '../../assets/wedly-home-13.png';
import categoryImg3 from '../../assets/wedly-home-6.jpeg';
import categoryImg4 from '../../assets/wedly-home-8.jpeg';
import categoryImg5 from '../../assets/wedly-home-4.png';
import categoryImg6 from '../../assets/wedly-home-14.png';
import categoryImg7 from '../../assets/wedly-home-15.jpeg';

import footerCtaImage from '../../assets/wedly-footer-cta.jpeg';

import WedlyWeddingHero from './WedlyWeddingHero';
import AISearchModalContent from './AISearchModalContent';
import css from './WedlyLandingPageV2.module.css';

/* ─── renderSizes hint for listing cards ─────────────────────────────────── */
const CARD_SIZES =
  '(max-width: 767px) calc(100vw - 36px), (max-width: 1024px) min(33vw, 320px), 240px';

const SECTION_ID = 'wedly-home-listings';
const LISTINGS_SECTION_DEF = [
  { sectionId: SECTION_ID, sectionType: 'listings', listingSelection: 'newest' },
];

/* ─── Arrow icon used in CTAs ────────────────────────────────────────────── */
const ArrowRight = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="10"
    viewBox="0 0 16 10"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M1 5h14M10 1l5 4-5 4"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AISparkleIcon = ({ className }) => (
  <svg className={className} width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path d="M7.5 1L8.6 5.4L13 6.5L8.6 7.6L7.5 12L6.4 7.6L2 6.5L6.4 5.4L7.5 1Z" fill="currentColor" />
    <path d="M12 1L12.5 3L14.5 3.5L12.5 4L12 6L11.5 4L9.5 3.5L11.5 3L12 1Z" fill="currentColor" opacity="0.6" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M2 2L16 16M16 2L2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/**
 * Lightweight AI search modal — renders WedlyWeddingHero inside a full-viewport
 * overlay. Does not require Redux; handles scroll-locking and Escape key locally.
 */
const AIModal = ({ isOpen, onClose, onSearch }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={css.aiOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Search with AI"
    >
      {/* Outer wrapper provides the animated gradient border */}
      <div className={css.aiDialogOuter} onClick={e => e.stopPropagation()}>
        {/* ── Modal header ─────────────────────────────────── */}
        <div className={css.aiModalHeader}>
          {/* Close sits in the corner, outside the text flow */}
          <button
            type="button"
            className={css.aiCloseBtn}
            onClick={onClose}
            aria-label="Close AI search"
          >
            <CloseIcon />
          </button>

          <AISparkleIcon className={css.aiModalHeaderIcon} />
          <h2 className={css.aiModalHeading}>Search Wedly with AI</h2>

          {/* Ornamental divider */}
          <div className={css.aiModalDivider} aria-hidden="true">
            <span className={css.aiModalDividerLine} />
            <span className={css.aiModalDividerDiamond}>◆</span>
            <span className={css.aiModalDividerLine} />
          </div>
        </div>

        {/* ── Scrollable search content ─────────────────────── */}
        <div className={css.aiDialog}>
          <AISearchModalContent onSearch={onSearch} />
        </div>
      </div>
    </div>
  );
};

/* ─── Category data ──────────────────────────────────────────────────────── */
const CATEGORIES = [
  {
    id: 'hens-bachelorettes',
    msgId: 'WedlyPage.cat.hensBachelorettes',
    img: categoryImg2,
  },
  {
    id: 'bridal-party',
    msgId: 'WedlyPage.cat.ceremonyDecor',
    img: categoryImg3,
  },
  {
    id: 'wedding-dress',
    msgId: 'WedlyPage.cat.weddingDress',
    img: categoryImg1,
  },
  {
    id: 'shoes',
    msgId: 'WedlyPage.cat.shoes',
    img: categoryImg5,
  },
  {
    id: 'flowers',
    msgId: 'WedlyPage.cat.flowers',
    img: categoryImg6,
  },
  {
    id: 'accessories',
    msgId: 'WedlyPage.cat.accessories',
    img: categoryImg7,
  },
  // {
  //   id: 'bridal-fashion',
  //   msgId: 'WedlyPage.cat.bridalFashion',
  //   img: categoryImg4,
  // },
  { id: 'table-decor', msgId: 'WedlyPage.cat.tableDecor', img: categoryImg4 },
];

/* ─── Journal article data ───────────────────────────────────────────────── */
const JOURNAL_ARTICLES = [
  {
    id: 'j1',
    typeId: 'WedlyPage.journal1Type',
    titleId: 'WedlyPage.journal1Title',
    img: editorialImg2,
  },
  {
    id: 'j2',
    typeId: 'WedlyPage.journal2Type',
    titleId: 'WedlyPage.journal2Title',
    img: editorialImg3,
  },
  {
    id: 'j3',
    typeId: 'WedlyPage.journal3Type',
    titleId: 'WedlyPage.journal3Title',
    img: editorialImg1,
  },
  {
    id: 'j4',
    typeId: 'WedlyPage.journal4Type',
    titleId: 'WedlyPage.journal4Title',
    img: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   Section components
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Hero section — full-bleed background image with overlaid editorial copy.
 * The imported heroImage URL is applied as an inline background-image so
 * webpack resolves the asset path correctly.
 */
const HeroSection = ({ onOpenAI }) => (
  <section
    className={css.hero}
    style={{
      '--hero-bg-desktop': `url(${heroImage})`,
      '--hero-bg-mobile': `url(${heroMobileImage})`,
    }}
    aria-label="Hero"
  >
    {/* Gradient scrim — bottom-heavy so copy stays legible */}
    <div className={css.heroOverlay} aria-hidden="true" />

    {/* Copy anchored to bottom-left */}
    <div className={css.heroInner}>
      <div className={css.heroContent}>
        <h1 className={css.heroHeading}>
          <FormattedMessage id="WedlyPage.heroHeading" />
        </h1>
        <p className={css.heroSubtitle}>
          <FormattedMessage id="WedlyPage.heroSubtitle" />
        </p>
        <div className={css.heroCtas}>
          <NamedLink name="SearchPage" className={css.heroShopNow}>
            <FormattedMessage id="WedlyPage.heroShopNow" />
          </NamedLink>
          <button type="button" className={css.heroShopWithAI} onClick={onOpenAI}>
            <AISparkleIcon />
            <FormattedMessage id="WedlyPage.shopWithAI" />
          </button>
        </div>
      </div>
    </div>
  </section>
);

/**
 * Pre-loved Marketplace — SEO intro section directly after the hero.
 */
const PreLovedSection = () => (
  <section className={css.preLoved} aria-labelledby="preloved-heading">
    <div className={css.seoSectionInner}>
      <h2 id="preloved-heading" className={css.seoSectionHeading}>
        <FormattedMessage id="WedlyPage.preLovedHeading" />
      </h2>
      <p className={css.seoSectionBody}>
        <FormattedMessage id="WedlyPage.preLovedBody1" />
      </p>
      <p className={css.seoSectionBody}>
        <FormattedMessage id="WedlyPage.preLovedBody2" />
      </p>
    </div>
  </section>
);

const WHY_WEDLY_ITEMS = [
  { titleId: 'WedlyPage.whyWedlyCuratedTitle', bodyId: 'WedlyPage.whyWedlyCuratedBody' },
  { titleId: 'WedlyPage.whyWedlyAustralianTitle', bodyId: 'WedlyPage.whyWedlyAustralianBody' },
  { titleId: 'WedlyPage.whyWedlyBuySellTitle', bodyId: 'WedlyPage.whyWedlyBuySellBody' },
  { titleId: 'WedlyPage.whyWedlySecureTitle', bodyId: 'WedlyPage.whyWedlySecureBody' },
  { titleId: 'WedlyPage.whyWedlyProtectionTitle', bodyId: 'WedlyPage.whyWedlyProtectionBody' },
];

/**
 * Why Wedly — value props grid.
 */
const WhyWedlySection = () => (
  <section className={css.whyWedly} aria-labelledby="why-wedly-heading">
    <div className={css.seoSectionInner}>
      <h2 id="why-wedly-heading" className={css.seoSectionHeading}>
        <FormattedMessage id="WedlyPage.whyWedlyHeading" />
      </h2>
      <div className={css.whyWedlyGrid}>
        {WHY_WEDLY_ITEMS.map(item => (
          <article key={item.titleId} className={css.whyWedlyItem}>
            <h3 className={css.whyWedlyItemTitle}>
              <FormattedMessage id={item.titleId} />
            </h3>
            <p className={css.whyWedlyItemBody}>
              <FormattedMessage id={item.bodyId} />
            </p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const FAQ_ITEMS = [
  { qId: 'WedlyPage.faq1Q', aId: 'WedlyPage.faq1A' },
  { qId: 'WedlyPage.faq2Q', aId: 'WedlyPage.faq2A' },
  { qId: 'WedlyPage.faq3Q', aId: 'WedlyPage.faq3A' },
  { qId: 'WedlyPage.faq4Q', aId: 'WedlyPage.faq4A' },
  { qId: 'WedlyPage.faq5Q', aId: 'WedlyPage.faq5A' },
];

/**
 * Homepage FAQs — static Q&A list for SEO and buyer confidence.
 */
const FaqSection = () => (
  <section className={css.faq} aria-labelledby="faq-heading">
    <div className={css.seoSectionInner}>
      <h2 id="faq-heading" className={css.seoSectionHeading}>
        <FormattedMessage id="WedlyPage.faqHeading" />
      </h2>
      <ul className={css.faqList}>
        {FAQ_ITEMS.map(item => (
          <li key={item.qId} className={css.faqItem}>
            <h3 className={css.faqQuestion}>
              <FormattedMessage id={item.qId} />
            </h3>
            <p className={css.faqAnswer}>
              <FormattedMessage id={item.aId} />
            </p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/**
 * Editor's Pick — two-column editorial feature.
 * Left: copy + CTA.  Right: stacked mosaic of 3 editorial photos.
 */
const EditorsPickSection = () => (
  <section className={css.editorsPick} aria-labelledby="ep-heading">
    <div className={css.editorsPickInner}>
      {/* Left copy */}
      <div className={css.editorsPickLeft}>
        <p className={css.sectionKicker}>
          <FormattedMessage id="WedlyPage.editorsPickKicker" />
        </p>
        <h2 id="ep-heading" className={css.editorsPickHeading}>
          <FormattedMessage id="WedlyPage.editorsPickHeading" />
        </h2>
        <p className={css.editorsPickBody}>
          <FormattedMessage id="WedlyPage.editorsPickBody" />
        </p>
        <NamedLink name="SearchPage" className={css.editorsPickCta}>
          <FormattedMessage id="WedlyPage.editorsPickCta" />
        </NamedLink>
      </div>

      {/* Right mosaic */}
      <div className={css.editorsMosaic}>
        <div className={css.mosaicLarge}>
          <img src={editorialImg1} alt="Wedding composition" className={css.mosaicImg} />
        </div>
        <div className={css.mosaicLarge}>
          <img src={editorialImg2} alt="Floral table arrangement" className={css.mosaicImg} />
        </div>
        <div className={css.mosaicLarge}>
          <img src={editorialImg3} alt="Wedding table setting" className={css.mosaicImg} />
        </div>
      </div>
    </div>
  </section>
);

/**
 * Shop by Category — editorial photo cards.
 */
const ShopByCategorySection = () => {
  const intl = useIntl();
  return (
    <section className={css.shopByCategory} aria-labelledby="sbc-heading">
      <div className={css.shopByCategoryInner}>
        <div className={css.shopByHeader}>
          <p className={css.sectionKicker}>
            <FormattedMessage id="WedlyPage.shopByKicker" />
          </p>
          <h2 id="sbc-heading" className={css.shopByHeading}>
            <FormattedMessage id="WedlyPage.shopByHeading" values={{ htmlBreak: <br /> }} />
          </h2>
        </div>

        <div className={css.categoryGrid}>
          {CATEGORIES.map(cat => (
            <NamedLink
              key={cat.id}
              name="SearchPage"
              to={{ search: `?pub_categoryLevel1=${cat.id}` }}
              className={css.categoryCard}
              aria-label={intl.formatMessage({ id: cat.msgId })}
            >
              <div className={css.categoryImgWrap}>
                <img src={cat.img} alt="" className={css.categoryImg} aria-hidden="true" />
              </div>
              <p className={css.categoryName}>
                <FormattedMessage id={cat.msgId} />
              </p>
              <span className={css.categoryShopNow}>
                <FormattedMessage id="WedlyPage.shopNow" />
              </span>
            </NamedLink>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Recent listings grid — shows newest marketplace listings.
 * Cards use {@link ListingCard}; description and category chips need extra `fields.listing` paths
 * on the featured-listings API query — see `getListingCardExtraQueryFields` and `featuredListings.duck.js`.
 */
const RecentListingsSection = ({ listings, inProgress }) => (
  <section className={css.recentListings} aria-labelledby="rl-heading">
    <div className={css.recentInner}>
      <div className={css.recentHeader}>
        <h2 id="rl-heading" className={css.shopByHeading}>
          <FormattedMessage id="WedlyPage.recentHeading" />
        </h2>
        <NamedLink name="SearchPage" className={css.viewAllLink}>
          <FormattedMessage id="WedlyPage.viewAll" />
          <ArrowRight className={css.viewAllArrow} />
        </NamedLink>
      </div>
      {inProgress ? (
        <div className={css.spinnerWrap}>
          <Loader size="large" />
        </div>
      ) : (
        <ul className={css.listingsGrid}>
          {listings.slice(0, 4).map(listing => (
            <li key={listing.id.uuid} className={css.listingsGridItem}>
              <ListingCard listing={listing} renderSizes={CARD_SIZES} lazyLoadImage />
            </li>
          ))}
        </ul>
      )}
    </div>
  </section>
);

/**
 * The Journal — editorial articles section.
 * Left column: label, heading, CTA.  Right: 4 article cards.
 */
const JournalSection = () => (
  <section className={css.journal} aria-labelledby="journal-heading">
    <div className={css.journalInner}>
      {/* Left editorial copy */}
      <div className={css.journalLeft}>
        <p className={css.sectionKicker}>
          <FormattedMessage id="WedlyPage.journalKicker" />
        </p>
        <h2 id="journal-heading" className={css.journalHeading}>
          <FormattedMessage id="WedlyPage.journalHeading" />
        </h2>
        <p className={css.journalBody}>
          <FormattedMessage id="WedlyPage.journalBody" />
        </p>
        <NamedLink name="BlogPage" className={css.journalCta}>
          <FormattedMessage id="WedlyPage.journalCta" />
        </NamedLink>
      </div>

      {/* Right: article cards */}
      <div className={css.journalRight}>
        <div className={css.journalCardsHeader}>
          <NamedLink name="BlogPage" className={css.journalViewAll}>
            <FormattedMessage id="WedlyPage.journalViewAll" />
            <ArrowRight className={css.viewAllArrow} />
          </NamedLink>
        </div>
        <div className={css.journalCards}>
          {JOURNAL_ARTICLES.map(article => (
            <NamedLink key={article.id} name="BlogPage" className={css.journalCard}>
              <div className={css.journalCardImgWrap}>
                <img src={article.img} alt="" className={css.journalCardImg} aria-hidden="true" />
              </div>
              <div className={css.journalCardMeta}>
                <p className={css.journalCardType}>
                  <FormattedMessage id={article.typeId} />
                </p>
                <p className={css.journalCardTitle}>
                  <FormattedMessage id={article.titleId} />
                </p>
              </div>
            </NamedLink>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/**
 * Footer CTA — dark full-width banner at the bottom.
 */
/**
 * Footer CTA — editorial two-column banner.
 * Left: warm cream background with heading, subtitle, and text link.
 * Right: full-bleed wedding photo.
 */
const FooterCtaSection = () => (
  <section
    className={css.footerCta}
    style={{ backgroundImage: `url(${footerCtaImage})` }}
    aria-labelledby="fcta-heading"
  >
    {/* Gradient scrim — left-heavy so copy stays legible */}
    <div className={css.footerCtaOverlay} aria-hidden="true" />

    {/* Copy */}
    <div className={css.footerCtaContent}>
      <h2 id="fcta-heading" className={css.footerCtaHeading}>
        <FormattedMessage id="WedlyPage.footerCtaHeading" values={{ htmlBreak: <br /> }} />
      </h2>
      <p className={css.footerCtaSubtitle}>
        <FormattedMessage id="WedlyPage.footerCtaSubtitle" />
      </p>
      <NamedLink name="SearchPage" className={css.footerCtaLink}>
        <FormattedMessage id="WedlyPage.footerCtaButton" />
      </NamedLink>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Page root
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * WedlyLandingPageV2
 *
 * Editorial wedding marketplace homepage replicating the luxury brand aesthetic
 * from the design reference image.
 * Sections: hero · pre-loved · editor's pick · shop by category ·
 *           recent listings · the journal · why wedly · FAQ · footer CTA.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.featuredListings — same shape as PageBuilder `options.featuredListings`
 */
const WedlyLandingPageV2 = props => {
  const { featuredListings } = props;
  const intl = useIntl();
  const config = useConfiguration();

  const [isAIOpen, setAIOpen] = useState(false);
  const openAI = useCallback(() => setAIOpen(true), []);
  const closeAI = useCallback(() => setAIOpen(false), []);
  const handleAISearch = useCallback(query => {
    setAIOpen(false);
    window.location.assign(`/ai-search?q=${encodeURIComponent(query)}`);
  }, []);

  const {
    onFetchFeaturedListings: fetchFeatured,
    getListingEntitiesById,
    parentPage,
    featuredListingData,
  } = featuredListings || {};

  const listingImageConfig = config.layout?.listingImage || {};
  const listingIds = featuredListingData?.[SECTION_ID]?.listingIds;
  const listingResolver = getListingEntitiesById || (() => []);
  const listings = listingIds ? listingResolver(listingIds) : [];
  const fetched = featuredListingData?.[SECTION_ID]?.fetched || false;
  const inProgress = featuredListingData?.[SECTION_ID]?.inProgress;
  const error = featuredListingData?.[SECTION_ID]?.error;

  useEffect(() => {
    if (!fetched && inProgress !== true && !error && typeof fetchFeatured === 'function') {
      fetchFeatured(SECTION_ID, parentPage, listingImageConfig, LISTINGS_SECTION_DEF);
    }
  }, [fetched, inProgress, error, fetchFeatured, parentPage, listingImageConfig]);

  const schemaTitle = intl.formatMessage({ id: 'WedlyLanding.schemaTitle' });
  const schemaDescription = intl.formatMessage({ id: 'WedlyLanding.schemaDescription' });

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(item => ({
      '@type': 'Question',
      name: intl.formatMessage({ id: item.qId }),
      acceptedAnswer: {
        '@type': 'Answer',
        text: intl.formatMessage({ id: item.aId }),
      },
    })),
  };

  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <StaticPage
      title={schemaTitle}
      description={schemaDescription}
      schema={[
        {
          '@type': 'WebSite',
          name: schemaTitle,
          description: schemaDescription,
        },
        faqSchema,
      ]}
    >
      <LayoutComposer areas={layoutAreas} className={pageBuilderCss.layout}>
        {composerProps => {
          const { Topbar, Main, Footer } = composerProps;
          return (
            <>
              <Topbar as="header" className={pageBuilderCss.topbar}>
                <TopbarContainer currentPage="LandingPage" />
              </Topbar>

              <Main
                as="main"
                id="main-content"
                className={classNames(pageBuilderCss.main, css.pageMain)}
              >
                <HeroSection onOpenAI={openAI} />
                <PreLovedSection />
                <EditorsPickSection />
                <ShopByCategorySection />
                {(listings.length > 0 || inProgress) && (
                  <RecentListingsSection listings={listings} inProgress={inProgress} />
                )}
                <JournalSection />
                <WhyWedlySection />
                <FaqSection />
                <FooterCtaSection />
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>

              <AIModal isOpen={isAIOpen} onClose={closeAI} onSearch={handleAISearch} />
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

WedlyLandingPageV2.propTypes = {
  featuredListings: PropTypes.shape({
    featuredListingData: PropTypes.object,
    parentPage: PropTypes.string,
    onFetchFeaturedListings: PropTypes.func,
    getListingEntitiesById: PropTypes.func,
  }),
};

export default WedlyLandingPageV2;
